import {faFolder} from "@fortawesome/pro-solid-svg-icons/faFolder";
import {faFolderUpload} from "@fortawesome/pro-solid-svg-icons/faFolderUpload";
import {faGrid} from "@fortawesome/pro-solid-svg-icons/faGrid";
import {faGrid2} from "@fortawesome/pro-solid-svg-icons/faGrid2";
import {faGrid3} from "@fortawesome/pro-solid-svg-icons/faGrid3";
import {faList} from "@fortawesome/pro-solid-svg-icons/faList";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {TourProvider} from "@reactour/tour";
import {GetItemCounts} from "@twigsby/client-data";
import {
    PageHeader,
    Button,
    ColourSolid,
    ContentLoader,
    DragHelper,
    DropdownMenu,
    FlexGap,
    FullWidthSidebar,
    InfiniteLoadTrigger,
    ItemRowHeader,
    RichEmptyState,
    StackHorizontal,
    StackVertical,
    ToggleSwitch,
    ToggleType,
    Tooltip,
    useLayout,
    useTranslation, FlexJustify,
} from "@twigsby/frontend-framework";
import {ItemRowToolBar} from "@twigsby/frontend-framework/lib/Components/ItemRow/ItemRowToolBar";
import {ElementUnderMousePointer, GetArrayIndexByKeyValue} from "@twigsby/helpers";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {NavLink} from "react-router-dom";
import {EndpointError} from "../../Components/EndpointError";
import {MailBoxImage} from "../../Components/Images/MailboxImage";
import {NoResultsImage} from "../../Components/Images/NoResultsImage";
import {MailboxFilter} from "../../Components/Mail/Controls/MailboxFilter";
import {MailboxInfo} from "../../Components/Mail/Controls/MailboxInfo";
import {
    CustomFolderDragOver,
    CustomFolderEnter,
    CustomFolderLeave,
    DisableFoldersById,
    ResetFolderStates
} from "../../Components/Mail/Events/CustomFolderEvents";
import {MailFilterPills} from "../../Components/Mail/Filter/MailFilterPills";
import {
    DraggingSelectorMouseDown,
    DraggingSelectorMousePos,
    DraggingSelectorMouseUp,
    GetMailEntriesInBetween,
    SetSelectedMailItemDetails,
    ShiftSelectMouseOver,
    ShiftSelectorDetector
} from "../../Components/Mail/Helpers";
import MailboxNavigation from "../../Components/Mail/MailboxNavigation";
import MailGridViewItem from "../../Components/Mail/MailGridViewItem";
import MailItem from "../../Components/Mail/MailItem";
import {SelectedMailMenuContent} from "../../Components/Mail/Menus/SelectedMailMenuContent";
import {SelectedMailToolbarContent} from "../../Components/Mail/Menus/SelectedMailToolbarContent";
import SelectEverythingBanner from "../../Components/SelectEverythingBanner";
import SelectionBar from "../../Components/SelectionBar";
import {useMailbox} from "../../Providers/NewMailboxProvider";
import {useSite} from "../../Providers/SiteInterfaceProvider";
import {mailboxTourSteps} from "../../Tours/Mailbox";

type MailboxPageProps = {
    isSearch?: boolean;
}

const GridSizeControl = () => {
    const { gridSize, SetGridSize } = useMailbox();
    return (
        <div className={'flex-row flex-gap-sm align-center ml-a mr-md'} >
            <FontAwesomeIcon icon={faGrid3} fixedWidth={true} />
            <input type={'range'} min={0} max={2} defaultValue={gridSize.size} onChange={(e) => SetGridSize(parseInt(e.target.value)) } />
            <FontAwesomeIcon icon={faGrid2} fixedWidth={true} />
        </div>
    )
}

const Mailboxes = (props: MailboxPageProps) => {
    const { t } = useTranslation();

    const {
        mailBoxInfo,

        status,
        data,
        error,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        dataUpdatedAt,

        customFolderData,

        MoveSelectedItems,
        gridSize,
        gridView,

        allSelected,
        visibleSelected,
        visibleIndeterminate,
        selectedItems,
        selectedItemsRef,

        userFilters,
        HandleFolderMove,

        ToggleAll,
        ToggleVisible,
        ToggleSelected,
        ResetSelection,
    } = useMailbox();

    const { currentLocale, updatePageMeta } = useSite();
    const { isTouchInput, isSmall, isMedium, isLarge, isXL } = useLayout();

    const [ tourStep, setTourStep] = useState(0);
    const [ tourFinished, setTourFinished ] = useState<boolean>(false);

    const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 } );

    const [draggingActive, setDraggingActive] = useState<boolean>(false);
    const [mouseDown, setMouseDown] = useState<boolean>(false);
    const [dragWasActive, setDragWasActive] = useState<boolean>(false);

    const [disableFolderSelection, setDisableFolderSelection] = useState<boolean>(false);

    const dragPositionRef = useRef<any>({ startX: 0, startY: 0, x: 0, y: 0 });
    const dragSelectorRef = useRef<HTMLDivElement>(null);
    const shiftKeyDownRef = useRef<boolean>(false);
    const mouseDownRef = useRef<boolean>(false);

    const mailBoxesRef = useRef<any>();
    const mailBoxNavigationRef = useRef<any>();
    const gridViewRef = useRef<boolean>(gridView);
    const draggingActiveRef = useRef<boolean>(draggingActive);
    const lastMousePosRef = useRef<any>({ x: 0, y: 0 });

    const [ mailBoxNavigationHeightRef, setMailBoxNavigationHeightRef ] = useState<number>(0);

    const MailItemDrag = useCallback((e: DragEvent) => {
        const target : HTMLElement | null = e.target as HTMLElement;
        if(!target) return;

        const targetId = target.getAttribute('data-item-id');

        if(!!targetId && target && e.dataTransfer && (target.matches('.item-row:not(.item-row-header)') || target.matches('.mailbox-item-block')) && e.button === 0) {
            if(!selectedItems.includes(targetId)) {
                ToggleSelected(targetId as string, itemCounts.total)
            }

            SetSelectedMailItemDetails(e, target.className, mailBoxInfo);

            if(mailBoxInfo.customFolderId) {
                DisableFoldersById(mailBoxInfo.customFolderId.toString());
            }

            setDraggingActive(true);
        }
    }, [data, selectedItems, mailBoxInfo]);

    const MailItemDragEnd = (e: DragEvent) => {
        e.preventDefault();

        if(e.clientX && e.clientY) {
            const target: Element | null = ElementUnderMousePointer(e.clientX, e.clientY) as HTMLElement;

            if(target && target.matches('.folder-entry:not(.folder-dropper-not-allowed), .custom-folder-list-entry:not(.folder-dropper-not-allowed)')) {
                MoveSelectedItems(parseInt(target.getAttribute('data-folder-id') as string));
            }
        }

        ResetFolderStates();
        setDraggingActive(false);
    }

    const HandleItemToggle = useCallback((selectedMailUuid: string, state?: boolean) => {
        if(shiftKeyDownRef.current && selectedItems.length > 0 && !gridViewRef.current) {
            const startEl = document.querySelector(`[data-item-id="${selectedItemsRef.current[selectedItemsRef.current.length - 1]}"]`);
            const endEl = document.querySelector(`[data-item-id="${selectedMailUuid}"]`);
            const parentEl = startEl?.parentElement || endEl?.parentElement;

            let inBetween: any = [];

            if(startEl !== endEl && startEl?.nextElementSibling && endEl) {
                const startIndex = Array.prototype.indexOf.call(parentEl?.children, startEl);
                const endIndex = Array.prototype.indexOf.call(parentEl?.children, endEl);
                inBetween = GetMailEntriesInBetween(startIndex > endIndex ? startEl.previousElementSibling : startEl.nextElementSibling, endEl, endIndex > startIndex);
            }

            for(const mailUuid of inBetween) {
                ToggleSelected(mailUuid, itemCounts.total);
            }
        } else {
            ToggleSelected(selectedMailUuid, itemCounts.total);
        }
    }, [selectedItems]);

    const HandleShiftKey = (e: KeyboardEvent) => {
        const shiftKeyActive = ShiftSelectorDetector(e, gridViewRef);

        shiftKeyDownRef.current = shiftKeyActive;
        setDisableFolderSelection(shiftKeyActive);

        if(e.shiftKey) {
            document.dispatchEvent(new Event('pointerover'));
        }
    }

    const HandleMouseOver =  useCallback((e: any) => {
        if(!mouseDownRef.current && selectedItemsRef.current.length > 0 && shiftKeyDownRef.current) {
            ShiftSelectMouseOver(e, selectedItemsRef, gridViewRef, lastMousePosRef);
        } else {
            document.querySelectorAll('.item-shift-active').forEach((item) => item.classList.remove('item-shift-active'))
        }
    }, [isLarge]);

    const HandleMousePos = useCallback((e: any) => {
        lastMousePosRef.current = { x : e.x || e.clientX, y: e.y || e.clientY };
        if(mouseDownRef.current && dragSelectorRef.current) {
            setDisableFolderSelection(true);
            DraggingSelectorMousePos(e, dragSelectorRef, dragPositionRef, mailBoxesRef, isLarge);
        }
    }, [isLarge]);

    const HandleMouseDown = useCallback((e: any) => {
        if(e.target.matches('.item-content-files') && e.button === 0) {
            DraggingSelectorMouseDown(e, mailBoxesRef, mouseDownRef, dragSelectorRef, dragPositionRef, isLarge);

            setMouseDown(true);
            setDragWasActive(true);
        }
    }, [isLarge]);

    const HandleMouseUp = (e: any) => {
        const shiftPreSelected = document.querySelectorAll('.item-shift-active');

        if(shiftKeyDownRef.current && shiftPreSelected.length > 0) {
            shiftPreSelected.forEach((item) => ToggleSelected(item.getAttribute('data-item-id') as string, itemCounts.total, true));
        }

        DraggingSelectorMouseUp(dragSelectorRef, mouseDownRef);

        setMouseDown(false);
        setTimeout(() => {
            setDisableFolderSelection(false);
        }, 200);
    }

    const SetDraggedSelected = () => {
        const selectedDrag = document.querySelectorAll('.item-drag-selected');

        for (let i = 0; i < selectedDrag.length; i++) {
            const id = selectedDrag[i].getAttribute('data-item-id');
            ToggleSelected(id, itemCounts.total, true);
        }

        setDragWasActive(false);
    }

    const HandleHeight = () => {
        if(!isLarge && mailBoxNavigationRef.current && mailBoxNavigationHeightRef !== mailBoxNavigationRef.current?.offsetHeight) {
            setMailBoxNavigationHeightRef(mailBoxNavigationRef.current?.offsetHeight);
        }
    }

    useEffect(() => {
        HandleHeight();
    }, [mailBoxNavigationRef]);

    useEffect(() => {
        if(data) {
            setItemCounts(GetItemCounts(data as any));
        }
    }, [dataUpdatedAt, data]);

    useEffect(() => {
        if(dragWasActive && selectedItems.length > 0 && !shiftKeyDownRef.current) {
            ResetSelection();
        }

        document.body.classList.toggle('dragging-active', draggingActive);

        draggingActiveRef.current = draggingActive;
    }, [draggingActive, dragWasActive]);

    useEffect(() => {
        if(dragWasActive) {
            if(!mouseDown) {
                SetDraggedSelected();
            } else if (selectedItems.length > 0 && !shiftKeyDownRef.current) {
                ResetSelection();
            }
        }
    }, [mouseDown, dragWasActive]);

    useEffect(() => {
        updatePageMeta({
            title: `${mailBoxInfo.folderName} - ${t('mailboxes.title')}`
        });
    }, [currentLocale, mailBoxInfo]);

    useEffect(() => {
        gridViewRef.current = gridView;
    }, [gridView]);

    useEffect(() => {
        window.addEventListener('orientationchange', HandleHeight);
        window.addEventListener('resize', HandleHeight);
        document.addEventListener('pointermove', HandleMousePos);
        document.addEventListener('pointerover', HandleMouseOver);
        document.addEventListener('pointerup', HandleMouseUp);
        document.addEventListener('keyup', HandleShiftKey);
        document.addEventListener('keydown', HandleShiftKey);

        return (() => {
            window.removeEventListener('orientationchange', HandleHeight);
            window.removeEventListener('resize', HandleHeight);
            document.removeEventListener('pointermove', HandleMousePos);
            document.removeEventListener('pointerover', HandleMouseOver);
            document.removeEventListener('pointerup', HandleMouseUp);
            document.removeEventListener('keyup', HandleShiftKey);
            document.removeEventListener('keydown', HandleShiftKey);
        })
    }, [isLarge]);

    return (
        <TourProvider
            steps={mailboxTourSteps}
            padding={0}
            currentStep={tourStep}
            setCurrentStep={(step) => setTourStep(step)}
            className={'surface-wrapper'}
            onClickClose={(e) => {
                e.setIsOpen(false);
                setTourFinished(true);
            }}
        >
            <FullWidthSidebar
                ref={mailBoxesRef}
                navigationComponent={
                    <MailboxNavigation
                        ref={mailBoxNavigationRef}
                        title={mailBoxInfo.folderName}
                        createFolderSuccessCallback={(e: any) => MoveSelectedItems(e)}
                        addonContent={
                            <>
                                {!isLarge && (
                                    <div className={'section-navigation-header-collapsed-addon flex-gap-xs'}>
                                        <RenderActions/>
                                    </div>
                                )}
                            </>
                        }
                    />
                }
                extraContentClass={gridView ? `item-content-files ${gridSize.className}` : `item-content-list`}
                onMouseDown={(e) => HandleMouseDown(e)}
                onTouchStart={(e) => HandleMouseDown(e)}
                extraClass={'mailbox-items-rows'}
            >
                {isLarge && (
                    <PageHeader title={mailBoxInfo.folderName}>
                        <div>
                            <div className={'flex-row flex-gap-sm'}>
                                <RenderActions/>
                            </div>
                        </div>
                    </PageHeader>
                )}

                {Object.entries(userFilters).length > 0 && data && data.pages[0].totalRecords > 0 && (
                    <ItemRowToolBar>
                        <MailFilterPills />
                    </ItemRowToolBar>
                )}

                <ItemRowHeader extraClass={selectedItems.length > 0 || gridView ? 'item-row-actions-active' : ''}
                               sticky={true}>
                    <div className='item-row-checkbox'>
                        <ToggleSwitch
                            name={`mailItem-all`}
                            label={`mailItem-all`}
                            invalid={false}
                            type={ToggleType.Checkbox}
                            defaultChecked={allSelected || !visibleIndeterminate && visibleSelected}
                            isIndeterminate={!allSelected && visibleIndeterminate}
                            onChange={(e) => ToggleVisible(data, 'mailUuid', !visibleSelected)}
                            hideLabel={true}
                        />
                    </div>

                    {selectedItems.length === 0 && !gridView && (
                        <>
                            <div>{t('mailboxes.headings.envelope')}</div>
                            {isSmall && (
                                <div>{t('mailboxes.headings.content')}</div>
                            )}
                            <div>{t('mailboxes.headings.reference')}</div>
                            {isMedium && (
                                <div>{mailBoxInfo.folderSlug === 'pending' ? t('mailboxes.headings.actionPending') : t('mailboxes.headings.pages')}</div>
                            )}
                            {isXL && (
                                <div>{t('mailboxes.headings.trackingIds')}</div>
                            )}
                            {isLarge && (
                                <div>{t('mailboxes.headings.tags')}</div>
                            )}
                            {isXL && (
                                <div>{t('mailboxes.headings.notes')}</div>
                            )}
                        </>
                    )}

                    {selectedItems.length === 0 && gridView && (
                        <GridSizeControl/>
                    )}

                    {selectedItems.length > 0 && (
                        <SelectionBar
                            selectedIds={selectedItems}
                            clearSelectionCallback={() => ResetSelection()}
                            iconsOnly={true}
                        >
                            {isMedium && (
                                <SelectedMailToolbarContent
                                    showCustomFolderRemove={!!mailBoxInfo.customFolderId && mailBoxInfo.customFolderId > -1}
                                />
                            )}

                            {!isMedium && (
                                <DropdownMenu
                                    trigger={
                                        <Button
                                            title={t('mailboxes.mailActions.title')}
                                            showArrow={true}
                                            squared={true}
                                            extraClass={'mail-actions-menu mr-xs'}
                                        />
                                    }
                                >
                                    <SelectedMailMenuContent
                                        showCustomFolderRemove={!!mailBoxInfo.customFolderId && mailBoxInfo.customFolderId > -1}
                                    />
                                </DropdownMenu>
                            )}

                        </SelectionBar>
                    )}
                </ItemRowHeader>

                {itemCounts.loaded !== itemCounts.total && selectedItems.length !== itemCounts.total && visibleSelected && (
                    <SelectEverythingBanner
                        selectedCount={allSelected ? itemCounts.total : selectedItems.length}
                        selectedLabel={allSelected ? t('actions.selectLoaded') : t('actions.selectAllIn', {location: mailBoxInfo.folderName})}
                        callback={() => ToggleAll(!allSelected)}
                    />
                )}

                <>
                    {status === "loading" ? (
                        <>
                            {gridView ? (
                                <>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                    <ContentLoader childCount={1} childWidth={'100%'} childHeight={'100%'}/>
                                </>
                            ) : (
                                <ContentLoader childHeight={'7em'}/>
                            )}
                        </>
                    ) : status === "error" && error ? (
                        <EndpointError {...error} stacked={true} extraClass={'m-md'} />
                    ) : (
                        <>
                            {customFolderData?.map((folder) => {
                                if (folder.parentFolderId === mailBoxInfo.customFolderId) {
                                    return (
                                        <NavLink
                                            style={{pointerEvents: disableFolderSelection ? 'none' : 'auto'}}
                                            to={`../mail/custom-folder/${folder.id}`}
                                            className={`custom-folder-list-entry ${gridView ? 'mailbox-item-block custom-folder-block' : 'item-row item-row-lg custom-folder-item'}`}
                                            data-folder-id={folder.id}
                                            draggable={false}
                                            onDragEnd={(e: any) => HandleFolderMove(e)}
                                            onDragEnter={(e: any) => CustomFolderEnter(e)}
                                            onDragLeave={(e: any) => CustomFolderLeave(e)}
                                            onDragOver={(e: any) => CustomFolderDragOver(e)}
                                        >
                                            <div className={`custom-folder-icon ${folder.colour}-outline`}>
                                                <FontAwesomeIcon icon={faFolder} size={gridView ? "5x" : "2x"} />
                                            </div>
                                            <StackVertical gap={FlexGap.ExtraSmall} extraClass={'custom-folder-meta'}>
                                                <span className={`custom-folder-item-title`}>{folder.description}</span>
                                                Child Folder - {t('counts.item', {count: folder.count})}
                                            </StackVertical>
                                        </NavLink>
                                    )
                                }

                                if (folder.parentFolderId && folder.id === mailBoxInfo.customFolderId) {
                                    const parentFolder = customFolderData[GetArrayIndexByKeyValue(customFolderData, 'id', folder.parentFolderId)];
                                    return (
                                        <NavLink
                                            style={{pointerEvents: disableFolderSelection ? 'none' : 'auto'}}
                                            to={`../mail/custom-folder/${parentFolder.id}`}
                                            className={`custom-folder-list-entry ${gridView ? 'mailbox-item-block custom-folder-block' : 'item-row item-row-lg custom-folder-item'}`}
                                            data-folder-id={parentFolder.id}
                                            draggable={false}
                                            onDragEnd={(e: any) => HandleFolderMove(e)}
                                            onDragEnter={(e: any) => CustomFolderEnter(e)}
                                            onDragLeave={(e: any) => CustomFolderLeave(e)}
                                            onDragOver={(e: any) => CustomFolderDragOver(e)}
                                        >
                                            <div className={`custom-folder-icon ${parentFolder.colour}-outline`}>
                                                <FontAwesomeIcon icon={faFolderUpload} size={gridView ? "5x" : "2x"} />
                                            </div>
                                            <StackVertical gap={FlexGap.ExtraSmall} extraClass={'custom-folder-meta'}>
                                                <span
                                                    className={`custom-folder-item-title`}>{parentFolder.description}</span>
                                                Parent Folder - {t('counts.item', {count: parentFolder.count})}
                                            </StackVertical>
                                        </NavLink>
                                    )
                                }
                            })}

                            {data?.pages.map((group, groupIndex) => {
                                if (Object.entries(userFilters).length > 0 && group.totalRecords === 0) {
                                    return (
                                        <RichEmptyState
                                            key={`mailItem${groupIndex}NoResults`}
                                            title={t('mailboxes.noResults.title')}
                                            description={t('mailboxes.noResults.description')}
                                            media={<NoResultsImage />}
                                            addon={
                                                <StackHorizontal justify={FlexJustify.Center} gap={FlexGap.Small} wrap={true} extraClass={'mb-lg'}>
                                                    <MailFilterPills hideLabel={true} />
                                                </StackHorizontal>
                                            }
                                        />
                                    )
                                }

                                if (group.totalRecords === 0) {
                                    return (
                                        <RichEmptyState
                                            key={`mailItem${groupIndex}NoItems`}
                                            title={t('mailboxes.noData.title', {folder: mailBoxInfo.folderName})}
                                            description={mailBoxInfo.folderId === 1
                                                ? t('mailboxes.noDataInbox.description') : mailBoxInfo.customFolderId
                                                    ? t('mailboxes.noDataCustomFolder.description') : t('mailboxes.noData.description')}
                                            media={<MailBoxImage/>}
                                        />
                                    )
                                }

                                return (
                                    group.data.map((mailItem, index) => {
                                        if (mailBoxInfo.customFolderId && mailItem.clientFolder?.parentFolder?.id === mailBoxInfo.customFolderId) return null;

                                        if (gridView) {
                                            return (
                                                <MailGridViewItem
                                                    key={`mailItem${groupIndex}${index}${mailItem.mailUuid}`}
                                                    index={`${groupIndex}_${index}`}
                                                    selected={allSelected || selectedItems.includes(mailItem.mailUuid)}
                                                    selectable={true}
                                                    callBack={(e: any, b: boolean) => HandleItemToggle(e, b)}
                                                    draggable={!isTouchInput && isLarge && customFolderData && customFolderData?.length > 0}
                                                    onDragStart={(e: DragEvent) => MailItemDrag(e)}
                                                    onDragEnd={(e: DragEvent) => MailItemDragEnd(e)}
                                                    onDragOver={(e: DragEvent) => e.preventDefault()}
                                                    shiftKey={shiftKeyDownRef}
                                                    {...mailItem}
                                                />
                                            )
                                        }

                                        return (
                                            <MailItem
                                                key={`mailItem${groupIndex}${index}${mailItem.mailUuid}`}
                                                index={`${groupIndex}_${index}`}
                                                selected={allSelected || selectedItems.includes(mailItem.mailUuid)}
                                                selectable={true}
                                                callBack={(e: any, b: boolean) => HandleItemToggle(e, b)}
                                                draggable={!isTouchInput && isLarge && customFolderData && customFolderData?.length > 0}
                                                onDragStart={(e: DragEvent) => MailItemDrag(e)}
                                                onDragEnd={(e: DragEvent) => MailItemDragEnd(e)}
                                                onDragOver={(e: DragEvent) => e.preventDefault()}
                                                shiftKey={shiftKeyDownRef}
                                                {...mailItem}
                                            />
                                        )
                                    })
                                )
                            })}

                            {itemCounts.total > 0 && (
                                <InfiniteLoadTrigger
                                    loaded={itemCounts.loaded}
                                    totalCount={itemCounts.total}
                                    hasNextPage={hasNextPage}
                                    fetchNextPage={fetchNextPage}
                                    isFetchingNextPage={isFetchingNextPage}
                                />
                            )}
                        </>
                    )}

                    <div ref={dragSelectorRef} className={'dragger-block'}/>
                </>
            </FullWidthSidebar>

            <DropdownMenu
                classActivators={['item-row', 'mailbox-item-block', 'mailbox-item-meta', 'mail-actions-menu']}
                classBlockers={['item-row-header', 'mail-item-forward-preview', 'custom-folder-list-entry']}
                contextActivated={true}
                activationCallBack={(el: any) => {
                    if (el.hasAttribute('data-item-id') && !selectedItems.includes(el.getAttribute('data-item-id'))) {
                        ToggleSelected(el.getAttribute('data-item-id'), itemCounts.total, true)
                    }
                }}
            >
                <SelectedMailMenuContent
                    showCustomFolderRemove={mailBoxInfo.customFolderId !== null && mailBoxInfo.customFolderId !== -1}
                />
            </DropdownMenu>

            <DragHelper selectedCount={selectedItems.length} />
        </TourProvider>
    );
};

export default Mailboxes;

const RenderActions = () => {
    const {
        gridView,
        SetGridView,
    } = useMailbox();

    const {t} = useTranslation();

    return (
        <>
            <MailboxInfo />
            <div className={'btn-group btn-group-row p-0'}>
                <Tooltip content={t('mailboxes.actions.listView')}>
                    <Button title={t('mailboxes.actions.listView')} iconOnly={true} icon={faList} colour={!gridView ? ColourSolid.Success : ColourSolid.Secondary} onClick={() => SetGridView(false)} />
                </Tooltip>
                <Tooltip content={t('mailboxes.actions.gridView')}>
                    <Button title={t('mailboxes.actions.gridView')} iconOnly={true} icon={faGrid} colour={gridView ? ColourSolid.Success : ColourSolid.Secondary} onClick={() => SetGridView(true)} />
                </Tooltip>
            </div>
            <MailboxFilter />
        </>
    )
}
